// Agent Browse Page — browse anonymous owner requests filtered by region
import { useState, useEffect } from "react";
import { api } from "@/services/api";
import { PageHeader, Card, StatusBadge, Button, LoadingSpinner, EmptyState } from "@/components/shared";
import { AgentResponseForm } from "@/components/agent/AgentResponseForm";
import { useAgentProfile } from "@/hooks/useAgentProfile";
import {
  Province,
  ProvinceLabels,
  PropertyType,
  PropertyTypeLabels,
  TimelineLabels,
  PPRAStatus,
  type OwnerRequest,
  type PaginatedResponse,
  type BrowseRequestsQuery,
} from "@/types";
import { Search, MapPin, Home, Clock, ShieldAlert, CheckCircle } from "lucide-react";

export function AgentBrowsePage() {
  const [requests, setRequests] = useState<OwnerRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<string | null>(null);
  const [filters, setFilters] = useState<BrowseRequestsQuery>({
    page: 1,
    limit: 20,
  });
  const { profile } = useAgentProfile();
  const [allowedRegions, setAllowedRegions] = useState<Province[]>([]);
  const isVerified = profile?.ppra_status === PPRAStatus.Verified;

  // Profile primary region is the source of truth for request access.
  useEffect(() => {
    if (profile?.primary_region) {
      setAllowedRegions([profile.primary_region as Province]);
    }
  }, [profile?.primary_region]);

  useEffect(() => {
    if (profile?.primary_region && !filters.province) {
      setFilters((f) => ({ ...f, province: profile.primary_region }));
    }
  }, [profile?.primary_region]);

  async function loadRequests() {
    setIsLoading(true);
    try {
      const query: Record<string, string | number | undefined> = {
        page: filters.page,
        limit: filters.limit,
      };
      if (filters.province) query.province = filters.province;
      if (filters.suburb) query.suburb = filters.suburb;
      if (filters.property_type) query.property_type = filters.property_type;

      const result = await api.get<PaginatedResponse<OwnerRequest>>("/browse-requests", query);
      setRequests(result.data);
    } catch {
      // handle error
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadRequests();
  }, [filters.province, filters.property_type, filters.page]);

  return (
    <div>
      <PageHeader
        title="Browse Owner Requests"
        description="View anonymous property requests in your region and submit proposals."
      />

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-sun" />
            <select
              value={filters.province || ""}
              onChange={(e) => setFilters((f) => ({ ...f, province: e.target.value as Province || undefined, page: 1 }))}
              className="bg-white/5 border border-white/20 rounded-md px-3 py-2 text-sm text-white focus:border-gold/50 focus:outline-none focus:ring-1 focus:ring-gold/30 appearance-none pr-8"
            >
              <option value="">All My Regions</option>
              {allowedRegions.map((p) => (
                <option key={p} value={p}>{ProvinceLabels[p]}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Home className="h-4 w-4 text-sun" />
            <select
              value={filters.property_type || ""}
              onChange={(e) => setFilters((f) => ({ ...f, property_type: e.target.value as PropertyType || undefined, page: 1 }))}
              className="bg-white/5 border border-white/20 rounded-md px-3 py-2 text-sm text-white focus:border-gold/50 focus:outline-none focus:ring-1 focus:ring-gold/30 appearance-none pr-8"
            >
              <option value="">All Types</option>
              {Object.values(PropertyType).map((t) => (
                <option key={t} value={t}>{PropertyTypeLabels[t]}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Filter by suburb..."
              value={filters.suburb || ""}
              onChange={(e) => setFilters((f) => ({ ...f, suburb: e.target.value || undefined }))}
              className="input"
            />
            <Button size="sm" onClick={loadRequests}>
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Request List */}
      {isLoading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : requests.length === 0 ? (
        <EmptyState
          icon={Search}
          title="No requests in this region"
          description="Try adjusting your filters or check back later for new owner requests."
        />
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <Card key={request.id}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-white">
                      {request.suburb}, {ProvinceLabels[request.province]}
                    </h3>
                    {PropertyTypeLabels[request.property_type] && (
                      <StatusBadge status={PropertyTypeLabels[request.property_type]} variant="info" />
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-white/50">
                    <span>R{(request.price_band_min / 1000000).toFixed(1)}M – R{(request.price_band_max / 1000000).toFixed(1)}M</span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {TimelineLabels[request.timeline]}
                    </span>
                    <span className="capitalize">{request.request_type}</span>
                  </div>
                  {request.seller_notes && (
                    <p className="mt-2 text-body italic">"{request.seller_notes}"</p>
                  )}
                </div>
                {request.my_response_status ? (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">
                    <CheckCircle className="h-4 w-4 text-emerald-400" />
                    <span className="text-sm font-medium capitalize text-emerald-400">
                      {request.my_response_status === "pending" ? "Responded" : request.my_response_status}
                    </span>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    disabled={!isVerified}
                    onClick={() => setSelectedRequest(selectedRequest === request.id ? null : request.id)}
                  >
                    {selectedRequest === request.id ? "Cancel" : "Respond"}
                  </Button>
                )}
              </div>

              {/* Verification gate */}
              {selectedRequest === request.id && !isVerified && (
                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                    <ShieldAlert className="h-5 w-5 text-red-400 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-red-400">Verification Required</p>
                      <p className="text-xs text-white/50 mt-0.5">Only PPRA-verified agents can respond to owner requests. Complete your profile and submit your FFC number for admin review.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Inline response form — only for verified agents */}
              {selectedRequest === request.id && isVerified && (
                <div className="mt-6 pt-6 border-t border-white/10">
                  <AgentResponseForm
                    requestId={request.id}
                    onSuccess={() => {
                      setSelectedRequest(null);
                      loadRequests();
                    }}
                  />
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
